import React, { useState } from 'react';
import { useAuth } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Eye, EyeOff, LogIn, UserPlus, Clock, CheckCircle } from 'lucide-react';
import { TwoFactorScreen } from '../components/login/TwoFactorScreen';
import { PasswordResetFlow } from '../components/login/PasswordResetFlow';
import { CLIENT_TYPES } from '../constants';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_f048c5ac-5226-4423-b21a-02c530902854/artifacts/xnmh0jmc_Design%20sans%20titre%20%282%29.png";
const BG_IMAGE = "https://images.pexels.com/photos/1173794/pexels-photo-1173794.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function LoginPage() {
  const { login, register, requires2FA, verify2FA, cancel2FA, pendingCredentials } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [resetMode, setResetMode] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    confirmPassword: '',
    // Beneficiary profile fields (optional / required mix)
    phone: '',
    contact_name: '',
    client_type: '',
    company_name: '',
    siret: '',
    address: '',
    website: '' // honeypot - must stay empty
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        const result = await login(formData.email, formData.password);
        if (result.requires_2fa) {
          toast.info('Authentification 2FA requise');
        } else {
          toast.success('Connexion réussie !');
        }
      } else {
        if (formData.password !== formData.confirmPassword) {
          toast.error('Les mots de passe ne correspondent pas');
          setLoading(false);
          return;
        }
        if (formData.password.length < 8) {
          toast.error('Le mot de passe doit contenir au moins 8 caractères');
          setLoading(false);
          return;
        }
        if (!formData.client_type) {
          toast.error('Veuillez sélectionner un type de bénéficiaire');
          setLoading(false);
          return;
        }
        if (!formData.phone.trim()) {
          toast.error('Le numéro de téléphone est obligatoire');
          setLoading(false);
          return;
        }
        if (!formData.contact_name.trim()) {
          toast.error('Le nom du contact principal est obligatoire');
          setLoading(false);
          return;
        }
        await register({
          email: formData.email,
          password: formData.password,
          first_name: formData.first_name,
          last_name: formData.last_name,
          phone: formData.phone,
          contact_name: formData.contact_name,
          client_type: formData.client_type,
          company_name: formData.company_name,
          siret: formData.siret,
          address: formData.address,
          website: formData.website // honeypot - blank for humans, filled by bots
        });
        setRegistrationSuccess(true);
        setFormData({
          email: '', password: '', first_name: '', last_name: '', confirmPassword: '',
          phone: '', contact_name: '', client_type: '', company_name: '', siret: '', address: '',
          website: ''
        });
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Une erreur est survenue';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // 2FA Screen
  if (requires2FA) {
    return (
      <TwoFactorScreen
        onSubmit={async (code) => {
          await verify2FA(code);
          toast.success('Connexion réussie !');
        }}
        onCancel={cancel2FA}
        pendingEmail={pendingCredentials?.email}
        bgImage={BG_IMAGE}
        logoUrl={LOGO_URL}
      />
    );
  }

  // Password Reset Flow
  if (resetMode) {
    return (
      <PasswordResetFlow
        onCancel={(email) => {
          setResetMode(false);
          if (email) setFormData(prev => ({ ...prev, email, password: '' }));
        }}
        initialEmail={formData.email}
        bgImage={BG_IMAGE}
        logoUrl={LOGO_URL}
      />
    );
  }

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="flex justify-center mb-8">
            <img src={LOGO_URL} alt="Diocèse de Pamiers" className="h-24 w-24 rounded-full shadow-lg bg-white p-2" data-testid="login-logo" />
          </div>

          {registrationSuccess && !isLogin && (
            <Alert className="mb-6 bg-emerald-50 border-emerald-200">
              <CheckCircle className="h-5 w-5 text-emerald-600" />
              <AlertDescription className="text-emerald-800 ml-2">
                <strong>Demande envoyée !</strong><br />
                Votre demande de création de compte a été enregistrée. 
                Vous recevrez un email dès que votre compte sera validé par l'équipe du diocèse.
              </AlertDescription>
            </Alert>
          )}

          <Card className="border-slate-200 shadow-lg">
            <CardHeader className="text-center pb-4">
              <CardTitle className="text-2xl text-[#1e3a8a]">
                {isLogin ? 'Connexion' : 'Créer un compte'}
              </CardTitle>
              <CardDescription className="text-slate-600">
                Gestion des réservations de salles
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!isLogin && !registrationSuccess && (
                <Alert className="mb-4 bg-blue-50 border-blue-200">
                  <Clock className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-blue-800 text-sm ml-2">
                    Les nouveaux comptes sont soumis à validation par l'équipe du diocèse.
                  </AlertDescription>
                </Alert>
              )}

              <form onSubmit={handleSubmit} className="space-y-4" autoComplete="on">
                {/* Honeypot: invisible to humans, irresistible to bots. Any value here rejects the request server-side. */}
                {!isLogin && (
                  <div
                    aria-hidden="true"
                    style={{ position: 'absolute', left: '-10000px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }}
                  >
                    <label htmlFor="website-hp">Site web (laissez vide)</label>
                    <input
                      type="text"
                      id="website-hp"
                      name="website"
                      tabIndex="-1"
                      autoComplete="off"
                      value={formData.website}
                      onChange={handleChange}
                      data-testid="register-honeypot-input"
                    />
                  </div>
                )}
                {!isLogin && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="first_name" className="text-slate-700">Prénom</Label>
                      <Input id="first_name" name="first_name" value={formData.first_name} onChange={handleChange} required={!isLogin} placeholder="Jean" className="border-slate-200 focus:ring-[#c5a059]" data-testid="register-firstname-input" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="last_name" className="text-slate-700">Nom</Label>
                      <Input id="last_name" name="last_name" value={formData.last_name} onChange={handleChange} required={!isLogin} placeholder="Dupont" className="border-slate-200 focus:ring-[#c5a059]" data-testid="register-lastname-input" />
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700">Email</Label>
                  <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required placeholder="jean.dupont@email.fr" className="border-slate-200 focus:ring-[#c5a059]" data-testid="login-email-input" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-700">Mot de passe</Label>
                  <div className="relative">
                    <Input id="password" name="password" type={showPassword ? 'text' : 'password'} value={formData.password} onChange={handleChange} required placeholder="••••••••" className="border-slate-200 focus:ring-[#c5a059] pr-10" data-testid="login-password-input" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="toggle-password-visibility">
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {!isLogin && <p className="text-xs text-slate-500">Minimum 8 caractères</p>}
                </div>

                {!isLogin && (
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword" className="text-slate-700">Confirmer le mot de passe</Label>
                    <Input id="confirmPassword" name="confirmPassword" type="password" value={formData.confirmPassword} onChange={handleChange} required={!isLogin} placeholder="••••••••" className="border-slate-200 focus:ring-[#c5a059]" data-testid="register-confirm-password-input" />
                  </div>
                )}

                {!isLogin && (
                  <>
                    <div className="pt-2 border-t border-slate-100">
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-3">Informations bénéficiaire</p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="client_type" className="text-slate-700">Type de bénéficiaire <span className="text-red-500">*</span></Label>
                      <Select value={formData.client_type} onValueChange={(v) => handleSelectChange('client_type', v)}>
                        <SelectTrigger id="client_type" className="border-slate-200 focus:ring-[#c5a059]" data-testid="register-client-type-select">
                          <SelectValue placeholder="Sélectionnez un type" />
                        </SelectTrigger>
                        <SelectContent>
                          {CLIENT_TYPES.map((t) => (
                            <SelectItem key={t.value} value={t.value} data-testid={`register-client-type-${t.value}`}>{t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-slate-700">Téléphone <span className="text-red-500">*</span></Label>
                        <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} required={!isLogin} placeholder="06 12 34 56 78" className="border-slate-200 focus:ring-[#c5a059]" data-testid="register-phone-input" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="contact_name" className="text-slate-700">Contact principal <span className="text-red-500">*</span></Label>
                        <Input id="contact_name" name="contact_name" value={formData.contact_name} onChange={handleChange} required={!isLogin} placeholder="Jean Dupont" className="border-slate-200 focus:ring-[#c5a059]" data-testid="register-contact-name-input" />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="company_name" className="text-slate-700">Raison sociale <span className="text-slate-400 text-xs">(facultatif)</span></Label>
                      <Input id="company_name" name="company_name" value={formData.company_name} onChange={handleChange} placeholder="Paroisse Saint-..., Association X, etc." className="border-slate-200 focus:ring-[#c5a059]" data-testid="register-company-name-input" />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="siret" className="text-slate-700">SIRET <span className="text-slate-400 text-xs">(facultatif)</span></Label>
                      <Input id="siret" name="siret" value={formData.siret} onChange={handleChange} placeholder="123 456 789 00012" maxLength={20} className="border-slate-200 focus:ring-[#c5a059]" data-testid="register-siret-input" />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="address" className="text-slate-700">Adresse <span className="text-slate-400 text-xs">(facultatif)</span></Label>
                      <Input id="address" name="address" value={formData.address} onChange={handleChange} placeholder="12 rue de la Paroisse, 09100 Pamiers" className="border-slate-200 focus:ring-[#c5a059]" data-testid="register-address-input" />
                    </div>
                  </>
                )}

                {isLogin && (
                  <div className="text-right">
                    <button type="button" onClick={() => setResetMode(true)} className="text-sm text-[#1e3a8a] hover:text-[#1e40af] hover:underline" data-testid="forgot-password-link">
                      Mot de passe oublié ?
                    </button>
                  </div>
                )}

                <Button type="submit" disabled={loading} className="w-full bg-[#1e3a8a] hover:bg-[#1e40af] text-white btn-animate" data-testid="login-submit-button">
                  {loading ? <div className="spinner mr-2" /> : isLogin ? <LogIn className="mr-2 h-4 w-4" /> : <UserPlus className="mr-2 h-4 w-4" />}
                  {isLogin ? 'Se connecter' : 'Demander un compte'}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <button type="button" onClick={() => { setIsLogin(!isLogin); setRegistrationSuccess(false); setFormData({ email: '', password: '', first_name: '', last_name: '', confirmPassword: '', phone: '', contact_name: '', client_type: '', company_name: '', siret: '', address: '', website: '' }); }} className="text-sm text-[#c5a059] hover:text-[#b08d47] font-medium transition-colors" data-testid="toggle-auth-mode">
                  {isLogin ? "Pas de compte ? Demander l'accès" : 'Déjà un compte ? Se connecter'}
                </button>
              </div>
              {/* Reset honeypot too when toggling modes */}
            </CardContent>
          </Card>

          <p className="text-center text-sm text-slate-500 mt-6">
            Diocèse de Pamiers - Gestion des Salles
          </p>
        </div>
      </div>

      <div className="hidden lg:block lg:w-1/2 bg-cover bg-center" style={{ backgroundImage: `url(${BG_IMAGE})` }}>
        <div className="h-full w-full bg-[#1e3a8a]/60 flex items-center justify-center p-12">
          <div className="text-center text-white max-w-lg">
            <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Merriweather, serif' }}>Bienvenue</h1>
            <p className="text-lg text-white/90">Plateforme de gestion des mises à disposition de salles du Diocèse de Pamiers. Simplifiez vos réservations, vos bénéficiaires et vos contributions en toute sérénité.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
